import React from 'react';
import { Controller } from 'react-hook-form';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Checkbox,
  Button,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import EditWarehouseTransition from './EditWarehouseTransition';

import { toggleEditWarehouseConfig } from 'atoms/ToggleEditWarehouse';
import { warehouseConfigState, warehouseObjState } from 'atoms/WarehouseObjState';

import * as Styles from './../AddWarehouse/styles';
import { CONFIG_FIELDS } from 'constants/warehouseConfig';
import useWarehouseConfigForm, { CONFIG_RULES } from 'hooks/Warehouse/useWarehouseConfigForm';

function EditWarehouseConfig() {
  const [showEdit, setShowEdit] = useRecoilState(toggleEditWarehouseConfig);
  const warehouse = useRecoilValue(warehouseObjState);
  const [warehouseConfig, setWarehouseConfig] = useRecoilState(warehouseConfigState);
  const { classes } = Styles.useStyles();

  const handleCancel = () => {
    setShowEdit(false);
  };

  const handleSaved = (data) => {
    setWarehouseConfig(data);
    handleCancel();
  };

  const {
    control,
    formState,
    fields,
    addTransition,
    removeTransition,
    applyDefaultLabelVersion,
    submit,
    submitting,
    pendingSave,
    confirmClearAndSave,
    keepEditing,
  } = useWarehouseConfigForm({ config: warehouseConfig, warehouse, onSaved: handleSaved });

  const renderConfigInput = (config) => {
    if (config.type === 'boolean') {
      return (
        <Grid key={config.field} size={4}>
          <Typography variant="subtitle2">{config.label}</Typography>
          <Controller
            control={control}
            name={config.field}
            render={({ field }) => (
              <Checkbox
                checked={!!field.value}
                color="primary"
                slotProps={{ input: { 'aria-label': config.label } }}
                onChange={(e) => field.onChange(e.target.checked)}
              />
            )}
          />
        </Grid>
      );
    }

    return (
      <Grid key={config.field} size={4}>
        <Typography variant="subtitle2">
          {config.label} {config.required && <strong style={{ color: 'red' }}>*</strong>}
        </Typography>
        <Controller
          control={control}
          name={config.field}
          rules={CONFIG_RULES[config.field]}
          render={({ field: { ref, ...field } }) => (
            <TextField
              {...field}
              inputRef={ref}
              id={config.field}
              value={field.value ?? ''}
              type={config.type === 'number' ? 'number' : 'text'}
              fullWidth
              size="small"
              error={!!formState.errors?.[config.field]}
              helperText={formState.errors?.[config.field]?.message ?? config.hint ?? ''}
              slotProps={{
                formHelperText: { sx: { ml: 0 } },
                htmlInput: { 'aria-label': config.label },
              }}
            />
          )}
        />
      </Grid>
    );
  };

  return (
    <Dialog open={showEdit} onClose={handleCancel} aria-labelledby="form-dialog-title" maxWidth="lg">
      <DialogTitle id="form-dialog-title" className={classes.Dialog}>
        Edit warehouse config
        <CloseIcon className={classes.closeBtn} onClick={handleCancel} />
      </DialogTitle>
      <DialogContent className={classes.formAdd} dividers>
        <form className={classes.root} noValidate autoComplete="off">
          <Grid container spacing={3}>
            {CONFIG_FIELDS.map((config) => renderConfigInput(config))}
            <Grid size={12}>
              <EditWarehouseTransition
                control={control}
                formState={formState}
                fields={fields}
                addTransition={addTransition}
                removeTransition={removeTransition}
                applyDefaultLabelVersion={applyDefaultLabelVersion}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions className={classes.dialogAction}>
        <Button onClick={handleCancel} variant="contained" color="inherit">
          CANCEL
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={formState.isSubmitted && Object.keys(formState.errors).length > 0}
          loading={submitting || formState.isSubmitting}
          onClick={submit}
        >
          SAVE
        </Button>
      </DialogActions>
      <Dialog open={!!pendingSave} onClose={keepEditing} maxWidth="sm">
        <DialogTitle>Clear target warehouses?</DialogTitle>
        <DialogContent>
          {(pendingSave?.clearedTransitions || []).map((cleared) => (
            <Typography key={cleared.index} sx={{ mb: 1, fontSize: 14 }}>
              Transition {cleared.index + 1} ({cleared.sortType}/{cleared.regions.join(', ')}): this will clear{' '}
              {cleared.count} target warehouses.
            </Typography>
          ))}
          <Typography sx={{ fontSize: 14 }}>Cleared target warehouses cannot be recovered. Save anyway?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={keepEditing} variant="contained" color="inherit" disabled={submitting}>
            Keep editing
          </Button>
          <Button onClick={confirmClearAndSave} variant="contained" color="error" loading={submitting}>
            Clear and save
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

export default EditWarehouseConfig;
